import type React from "react";
import type { ReactNode } from "react";
import type {
  CardApprovalFailure,
  CardUnion,
  CardKind,
  CardOfKind,
  CardActionHandler,
  CardViewState,
} from "./types";

type CardComponent<K extends CardKind> = React.FC<{
  card: CardOfKind<K>;
  onAction?: CardActionHandler;
  /** Cards that carry an approval read it; the others ignore it. */
  state?: CardViewState;
  /** Set when the approval this card carried was refused; the card re-opens
   * with the messages instead of a compact summary. */
  failure?: CardApprovalFailure;
}>;

type Registry = { [K in CardKind]?: CardComponent<K> };

const REGISTRY: Registry = {};

export function registerCardComponent<K extends CardKind>(
  kind: K,
  Component: CardComponent<K>,
): void {
  // The mapped Registry type is invariant in K (each slot demands its own
  // CardComponent<K>), and TypeScript cannot prove the per-call K narrows
  // the entire registry. Cast through unknown — runtime safety is preserved
  // because `kind` is the discriminator used to look the component up.
  (REGISTRY as Record<CardKind, unknown>)[kind] = Component;
}

export function renderCard(
  card: CardUnion,
  onAction?: CardActionHandler,
  state?: CardViewState,
  failure?: CardApprovalFailure,
): ReactNode | null {
  const Component = REGISTRY[card.kind] as CardComponent<typeof card.kind> | undefined;
  if (!Component) return null;
  return (
    <Component
      card={card as CardOfKind<typeof card.kind>}
      onAction={onAction}
      state={state}
      failure={failure}
    />
  );
}

/** Test-only — clears state between tests. Do not use in app code. */
export function __resetRegistryForTests(): void {
  (Object.keys(REGISTRY) as CardKind[]).forEach((k) => delete REGISTRY[k]);
}
